import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Columns, MessageSquare, CheckCircle, Clock, User } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import openSocket from "../../services/socket-io";

const statusColors = {
  open:    { cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", label: "Abierto" },
  pending: { cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",       label: "Pendiente" },
  closed:  { cls: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",           label: "Resuelto" },
};

const getInitials = (name) =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().substring(0, 2);

const TicketCard = ({ ticket, onDragStart, onDragEnd, onClick }) => {
  const sc = statusColors[ticket.status] || statusColors.open;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, ticket)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(ticket)}
      className="relative bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-3 mb-2 cursor-grab hover:border-indigo-400 dark:hover:border-indigo-500 hover:-translate-y-0.5 hover:shadow-md transition-all select-none"
    >
      {ticket.unreadMessages > 0 && (
        <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
          {ticket.unreadMessages}
        </span>
      )}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
          {getInitials(ticket.contact?.name || "?")}
        </div>
        <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate flex-1">
          {ticket.contact?.name || "Sin nombre"}
        </span>
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sc.cls}`}>
          {sc.label}
        </span>
      </div>
      {ticket.lastMessage && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 line-clamp-2">
          {ticket.lastMessage}
        </p>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full">
          <User className="w-3 h-3" />
          {ticket.user?.name || "Sin asignar"}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
          <Clock className="w-3 h-3" />
          {format(parseISO(ticket.updatedAt), "HH:mm")}
        </span>
      </div>
    </div>
  );
};

const KanbanColumn = ({ title, icon: Icon, iconColor, headerCls, count, columnId, onDragOver, onDrop, children }) => {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div className="min-w-[280px] sm:min-w-[300px] max-w-[300px] snap-center shrink-0 flex flex-col rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 overflow-hidden">
      <div className={`px-4 py-3 border-b-2 ${headerCls}`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="font-bold text-sm text-slate-900 dark:text-slate-100">{title}</span>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-semibold">
          {count} {count === 1 ? "ticket" : "tickets"}
        </p>
      </div>
      <div
        className={`flex-1 p-3 overflow-y-auto min-h-40 transition-colors ${dragOver ? "bg-indigo-50/50 dark:bg-indigo-950/20" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); onDragOver(e); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { setDragOver(false); onDrop(e, columnId); }}
      >
        {children}
      </div>
    </div>
  );
};

const Kanban = () => {
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [queues, setQueues] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedTicket, setDraggedTicket] = useState(null);

  useEffect(() => {
    fetchQueues();
    fetchTickets();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    const handleTicket = (data) => {
      if (data.action === "update" || data.action === "create") {
        setTickets((prev) => {
          const idx = prev.findIndex((t) => t.id === data.ticket.id);
          if (idx !== -1) { const n = [...prev]; n[idx] = data.ticket; return n; }
          return [data.ticket, ...prev];
        });
      }
      if (data.action === "delete")
        setTickets((prev) => prev.filter((t) => t.id !== data.ticketId));
    };

    socket.on("ticket", handleTicket);

    return () => {
      socket.off("ticket", handleTicket);
    };
  }, []);

  const fetchQueues = async () => {
    try { const { data } = await api.get("/queue"); setQueues(data); }
    catch (err) { toastError(err); }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tickets", { params: { status: "open,pending,closed", showAll: true } });
      setTickets(data.tickets);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  const handleDragStart = (e, ticket) => {
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => setDraggedTicket(null);
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const handleDrop = async (e, targetQueueId) => {
    e.preventDefault();
    if (!draggedTicket) return;

    const originalQueueId = draggedTicket.queueId;
    const originalStatus = draggedTicket.status;
    let updateData, optimisticUpdate;

    if (targetQueueId === "closed") {
      if (originalStatus === "closed") return;
      updateData = optimisticUpdate = { status: "closed" };
    } else {
      const newQueueId = targetQueueId === "unassigned" ? null : targetQueueId;
      if (originalQueueId === newQueueId && originalStatus !== "closed") return;
      updateData = optimisticUpdate = { status: "open", queueId: newQueueId };
    }

    setTickets((prev) => prev.map((t) => t.id === draggedTicket.id ? { ...t, ...optimisticUpdate } : t));

    try {
      await api.put(`/tickets/${draggedTicket.id}`, updateData);
      toast.success("Ticket actualizado exitosamente");
    } catch (err) {
      setTickets((prev) => prev.map((t) => t.id === draggedTicket.id ? { ...t, queueId: originalQueueId, status: originalStatus } : t));
      toastError(err);
    }
  };

  const handleTicketClick = (ticket) => history.push(`/tickets/${ticket.id}`);

  const getTicketsByQueue = (queueId) => tickets.filter((t) => t.queueId === queueId && t.status !== "closed");
  const getUnassignedTickets = () => tickets.filter((t) => !t.queueId && t.status !== "closed");
  const getClosedTickets = () => tickets.filter((t) => t.status === "closed");

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <span className="flex items-center gap-2">
            <Columns className="w-5 h-5" />
            Embudos
          </span>
        </Title>
      </MainHeader>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto px-3 sm:px-6 pb-6 snap-x">
          <div className="flex gap-4 h-full min-h-0 pt-1">
            <KanbanColumn
              title="Sin Asignar"
              icon={MessageSquare}
              iconColor="text-amber-500"
              headerCls="bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700"
              count={getUnassignedTickets().length}
              columnId="unassigned"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {getUnassignedTickets().map((t) => (
                <TicketCard key={t.id} ticket={t} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onClick={handleTicketClick} />
              ))}
              {getUnassignedTickets().length === 0 && (
                <p className="text-center text-xs text-slate-400 mt-8">Sin tickets</p>
              )}
            </KanbanColumn>

            {queues.map((queue) => (
              <KanbanColumn
                key={queue.id}
                title={queue.name}
                icon={({ className }) => (
                  <span className={`w-3.5 h-3.5 rounded-full flex-shrink-0 ${className}`} style={{ backgroundColor: queue.color }} />
                )}
                iconColor=""
                headerCls="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                count={getTicketsByQueue(queue.id).length}
                columnId={queue.id}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {getTicketsByQueue(queue.id).map((t) => (
                  <TicketCard key={t.id} ticket={t} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onClick={handleTicketClick} />
                ))}
                {getTicketsByQueue(queue.id).length === 0 && (
                  <p className="text-center text-xs text-slate-400 mt-8">Sin tickets</p>
                )}
              </KanbanColumn>
            ))}

            <KanbanColumn
              title="Resueltos"
              icon={CheckCircle}
              iconColor="text-slate-400"
              headerCls="bg-slate-100 dark:bg-slate-800/50 border-slate-300 dark:border-slate-600"
              count={getClosedTickets().length}
              columnId="closed"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {getClosedTickets().map((t) => (
                <TicketCard key={t.id} ticket={t} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onClick={handleTicketClick} />
              ))}
              {getClosedTickets().length === 0 && (
                <p className="text-center text-xs text-slate-400 mt-8">Sin tickets</p>
              )}
            </KanbanColumn>
          </div>
        </div>
      )}
    </MainContainer>
  );
};

export default Kanban;
